import { useStore } from '@/store/useStore';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Trash2, Plus, X, Brackets } from 'lucide-react';
import { cn } from '@/utils/cn';
import { downloadTableExcel } from '@/utils/exportTable';
import {
  FUNNEL_STATUS, EVENT_PLACE_LABEL, SCREEN_TYPE_LABEL, DEAL_TO_FUNNEL, LANGUAGE_OPTIONS,
  effectiveAmount,
  type Deal,
} from '@/types';

const LINE_COLORS = ['#7F77DD', '#9B9B9B', '#22C3E6', '#1D9E75', '#D85A30', '#D4537E'];

type GroupBy = 'day' | 'week' | 'month';
type DateBasis = 'sale' | 'event';
type Agg = 'count' | 'sum' | 'avg' | 'max' | 'min';

const GROUP_LABEL: Record<GroupBy, string> = { day: 'По дням', week: 'По неделям', month: 'По месяцам' };
const BASIS_LABEL: Record<DateBasis, string> = { sale: 'По дате продажи', event: 'По дате события' };
const AGG_LABEL: Record<Agg, string> = { count: 'Количество игр', sum: 'Сумма за игры', avg: 'Средняя цена игры', max: 'Максимальная цена игры', min: 'Минимальная цена игры' };

type Cond = { id: string; param: string; value: string };
type Group = { id: string; op: 'AND' | 'OR'; conds: Cond[] };
type Line = { id: string; name: string; op: 'AND' | 'OR'; groups: Group[] };

let _id = 0;
const uid = () => `r${++_id}`;
const newGroup = (): Group => ({ id: uid(), op: 'AND', conds: [{ id: uid(), param: '', value: '' }] });
const newLine = (): Line => ({ id: uid(), name: '', op: 'AND', groups: [newGroup()] });

export function ReportsPage() {
  const { deals, game_formats, sources, toast, filterBranchId } = useStore();

  const [groupBy, setGroupBy] = useState<GroupBy>('month');
  const [basis, setBasis] = useState<DateBasis>('sale');
  const [agg, setAgg] = useState<Agg>('count');
  // По умолчанию считаем по сумме менеджера (amount). Тумблер переключает на фактическую
  // сумму (effectiveAmount = факт с подстрахой суммой менеджера, если касса не вписала).
  const [useFact, setUseFact] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [lines, setLines] = useState<Line[]>([newLine()]);
  const [active, setActive] = useState(0);
  const [built, setBuilt] = useState<{ periods: string[]; series: { name: string; color: string; values: number[] }[] } | null>(null);

  // Реестр параметров условий (Параметр → варианты значений + проверка сделки).
  const PARAMS = useMemo(() => [
    { key: 'status', label: 'Статус заказа', options: FUNNEL_STATUS.map((s) => ({ value: s.key, label: s.label })), match: (d: Deal, v: string) => (d.funnel_status ?? DEAL_TO_FUNNEL[d.status]) === v },
    { key: 'format', label: 'Формат игры', options: game_formats.map((g) => ({ value: g.id, label: g.name })), match: (d: Deal, v: string) => d.event.game_format_id === v },
    { key: 'place', label: 'Место проведения', options: (Object.keys(EVENT_PLACE_LABEL) as (keyof typeof EVENT_PLACE_LABEL)[]).map((p) => ({ value: p, label: EVENT_PLACE_LABEL[p] })), match: (d: Deal, v: string) => d.event.place === v },
    // Язык мультизначный («Казахский, Русский») — сделка совпадает с группой языка, если он есть среди её языков.
    { key: 'language', label: 'Язык', options: LANGUAGE_OPTIONS.map((l) => ({ value: l, label: l })), match: (d: Deal, v: string) => (d.event.language ?? '').split(',').map((s) => s.trim()).includes(v) },
    { key: 'screen', label: 'Экран на площадке', options: (Object.keys(SCREEN_TYPE_LABEL) as (keyof typeof SCREEN_TYPE_LABEL)[]).map((s) => ({ value: s, label: SCREEN_TYPE_LABEL[s] })), match: (d: Deal, v: string) => d.event.screen_type === v },
    { key: 'source', label: 'Откуда узнали', options: sources.map((s) => ({ value: s.id, label: s.name })), match: (d: Deal, v: string) => d.source_id === v },
    { key: 'played', label: 'Играли ранее', options: [{ value: 'да', label: 'да' }, { value: 'нет', label: 'нет' }], match: (d: Deal, v: string) => (d.event.played_before ? 'да' : 'нет') === v },
  ], [game_formats, sources]);
  const paramByKey = useMemo(() => new Map(PARAMS.map((p) => [p.key, p])), [PARAMS]);

  // ── изменение линий / групп / условий ──
  const updLine = (i: number, patch: Partial<Line>) => setLines((p) => p.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => { setLines((p) => [...p, newLine()]); setActive(lines.length); };
  const delLine = (i: number) => { setLines((p) => p.filter((_, idx) => idx !== i)); setActive(0); };
  const updGroup = (i: number, gi: number, patch: Partial<Group>) => updLine(i, { groups: lines[i].groups.map((g, idx) => idx === gi ? { ...g, ...patch } : g) });
  const addGroup = (i: number) => updLine(i, { groups: [...lines[i].groups, newGroup()] });
  const delGroup = (i: number, gi: number) => updLine(i, { groups: lines[i].groups.filter((_, idx) => idx !== gi) });
  const addCond = (i: number, gi: number) => updGroup(i, gi, { conds: [...lines[i].groups[gi].conds, { id: uid(), param: '', value: '' }] });
  const updCond = (i: number, gi: number, ci: number, patch: Partial<Cond>) => updGroup(i, gi, { conds: lines[i].groups[gi].conds.map((c, idx) => idx === ci ? { ...c, ...patch } : c) });
  const delCond = (i: number, gi: number, ci: number) => updGroup(i, gi, { conds: lines[i].groups[gi].conds.filter((_, idx) => idx !== ci) });

  // ── построение ──
  const groupMatch = (g: Group, d: Deal) => {
    const act = g.conds.filter((c) => c.param && c.value);
    if (act.length === 0) return true;
    const results = act.map((c) => paramByKey.get(c.param)?.match(d, c.value) ?? false);
    return g.op === 'AND' ? results.every(Boolean) : results.some(Boolean);
  };
  const lineMatch = (l: Line, d: Deal) => {
    const groups = l.groups.filter((g) => g.conds.some((c) => c.param && c.value));
    if (groups.length === 0) return true;
    const results = groups.map((g) => groupMatch(g, d));
    return l.op === 'AND' ? results.every(Boolean) : results.some(Boolean);
  };
  const dealTs = (d: Deal) => basis === 'sale' ? d.created_at : (d.event.start_at ?? d.event.event_date ?? d.created_at);

  const build = () => {
    const fromTs = from ? new Date(from).getTime() : -Infinity;
    const toTs = to ? new Date(to).getTime() + 24 * 3600_000 : Infinity;
    // Учитываем верхний выбор филиала (как дашборд/календарь): «Все филиалы» — по всем
    // городам, иначе только сделки выбранного филиала. Читаем при построении графика.
    const inRange = deals.filter((d) => {
      if (filterBranchId !== 'all' && d.branch_id !== filterBranchId) return false;
      const t = dealTs(d);
      return t >= fromTs && t < toTs;
    });
    const periods = genPeriods(from, to, groupBy, inRange.map(dealTs));
    const series = lines.map((l, i) => {
      const matched = inRange.filter((d) => lineMatch(l, d));
      const byBucket = new Map<string, number[]>();
      for (const d of matched) {
        const k = bucketKey(dealTs(d), groupBy);
        const arr = byBucket.get(k) ?? []; arr.push(useFact ? effectiveAmount(d) : (d.amount ?? 0)); byBucket.set(k, arr);
      }
      const values = periods.map((p) => aggregate(byBucket.get(p) ?? [], agg));
      return { name: l.name.trim() || `Линия ${i + 1}`, color: LINE_COLORS[i % LINE_COLORS.length], values };
    });
    setBuilt({ periods, series });
  };

  const yLabel = agg === 'count' ? 'Количество' : 'Сумма, ₸';

  // Выгрузка таблицы данных отчёта (Excel открывает чисто — см. utils/exportTable).
  const exportExcel = () => {
    if (!built) return;
    const head = ['Период', ...built.series.map((s) => s.name)];
    const rows = built.periods.map((p, pi) => [p, ...built.series.map((s) => Math.round(s.values[pi]))]);
    downloadTableExcel(`Отчёт_${new Date().toISOString().slice(0, 10)}.xlsx`, head, rows);
    toast({ kind: 'success', text: 'Отчёт выгружен' });
  };

  return (
    <div className="h-full overflow-y-auto bg-surface-0">
      <div className="max-w-[1400px] mx-auto p-3 sm:p-5 space-y-5">
        {/* Параметры агрегации */}
        <div className="panel p-5">
          <h2 className="text-lg font-semibold text-ink-2 mb-4">Параметры агрегации</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <Select label="Группировка" value={groupBy} onChange={(v) => setGroupBy(v as GroupBy)} options={Object.entries(GROUP_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            <Select label="Дата" value={basis} onChange={(v) => setBasis(v as DateBasis)} options={Object.entries(BASIS_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
            <Select label="Агрегация" value={agg} onChange={(v) => setAgg(v as Agg)} options={Object.entries(AGG_LABEL).map(([v, l]) => ({ value: v, label: l }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <DateInput value={from} onChange={setFrom} />
            <DateInput value={to} onChange={setTo} />
          </div>
          {/* Считать по фактической сумме (касса), а не по сумме менеджера. Влияет на
              суммовые агрегации (сумма/средняя/макс/мин); «Количество игр» не зависит. */}
          <label className="flex items-center gap-2 text-sm text-ink-2 mt-3 cursor-pointer select-none">
            <input type="checkbox" className="w-4 h-4 accent-accent" checked={useFact} onChange={(e) => setUseFact(e.target.checked)} />
            Считать по фактической сумме (касса)
          </label>
          <div className="flex justify-end mt-4">
            <button className="btn-primary px-6" onClick={build}>Построить графики</button>
          </div>
        </div>

        {/* Данные — конструктор линий */}
        <div className="panel p-5">
          <h2 className="text-lg font-semibold text-ink-2 mb-4">Данные</h2>
          <div className="flex items-center gap-4 border-b border-line mb-4">
            {lines.map((l, i) => (
              <button key={l.id} onClick={() => setActive(i)}
                className={cn('pb-2 text-sm font-semibold uppercase tracking-wide border-b-2 -mb-px', active === i ? 'border-accent text-accent' : 'border-transparent text-ink-3 hover:text-ink-1')}>
                {l.name.trim() || `Линия ${i + 1}`}
              </button>
            ))}
            <button onClick={addLine} className="pb-2 text-sm font-semibold text-accent flex items-center gap-1"><Plus className="w-4 h-4" /> ДОБАВИТЬ ЛИНИЮ</button>
          </div>

          {lines[active] && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <input className="input flex-1 max-w-[480px]" placeholder={`Линия ${active + 1}`} value={lines[active].name} onChange={(e) => updLine(active, { name: e.target.value })} />
                {lines.length > 1 && (
                  <button className="text-status-refused font-semibold text-sm flex items-center gap-1.5" onClick={() => delLine(active)}><Trash2 className="w-4 h-4" /> УДАЛИТЬ</button>
                )}
              </div>

              <div className="border-l-2 border-accent/40 pl-3 space-y-3">
                {/* Как объединять группы между собой (если их больше одной) */}
                {lines[active].groups.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ink-3 uppercase tracking-wide">Группы объединять:</span>
                    <select className="input w-28" value={lines[active].op} onChange={(e) => updLine(active, { op: e.target.value as 'AND' | 'OR' })}>
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </select>
                  </div>
                )}

                {lines[active].groups.map((g, gi) => (
                  <div key={g.id} className="rounded-xl border border-line p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <select className="input flex-1" value={g.op} onChange={(e) => updGroup(active, gi, { op: e.target.value as 'AND' | 'OR' })}>
                        <option value="AND">AND</option>
                        <option value="OR">OR</option>
                      </select>
                      <button className="icon-btn text-accent" title="Добавить условие" onClick={() => addCond(active, gi)}><Plus className="w-4 h-4" /></button>
                      {lines[active].groups.length > 1 && (
                        <button className="icon-btn text-ink-3 hover:text-status-refused" title="Удалить группу" onClick={() => delGroup(active, gi)}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                    {g.conds.map((c, ci) => {
                      const p = paramByKey.get(c.param);
                      return (
                        <div key={c.id} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <select className="input flex-1" value={c.param} onChange={(e) => updCond(active, gi, ci, { param: e.target.value, value: '' })}>
                              <option value="">Параметр</option>
                              {PARAMS.map((pp) => <option key={pp.key} value={pp.key}>{pp.label}</option>)}
                            </select>
                            <button className="icon-btn text-ink-3 hover:text-status-refused" onClick={() => delCond(active, gi, ci)}><X className="w-4 h-4" /></button>
                          </div>
                          {p && (
                            <select className="input w-full" value={c.value} onChange={(e) => updCond(active, gi, ci, { value: e.target.value })}>
                              <option value="">— значение —</option>
                              {p.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}

                <button onClick={() => addGroup(active)} className="text-sm font-semibold text-accent flex items-center gap-1.5"><Brackets className="w-4 h-4" /> ДОБАВИТЬ ГРУППУ (СКОБКИ)</button>
              </div>
            </div>
          )}
        </div>

        {/* Графики */}
        {built && (
          <div className="panel p-5">
            <h2 className="text-lg font-semibold text-ink-2 mb-4">Графики</h2>
            <LineChart periods={built.periods} series={built.series} yLabel={yLabel} isCount={agg === 'count'} />
          </div>
        )}

        {/* Таблица данных */}
        {built && (
          <div className="panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-ink-2">Таблица данных</h2>
              <button className="btn-soft" onClick={exportExcel}>Выгрузить в Excel</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-ink-3 border-b border-line">
                    <th className="px-3 py-2 font-medium">Период</th>
                    {built.series.map((s, i) => <th key={i} className="px-3 py-2 font-medium">{s.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {built.periods.map((p, pi) => (
                    <tr key={p} className="border-b border-line/50">
                      <td className="px-3 py-2 text-ink-2 tabular-nums">{p}</td>
                      {built.series.map((s, i) => <td key={i} className="px-3 py-2 text-ink-1 tabular-nums">{fmtVal(s.values[pi], agg === 'count')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── График (SVG, мультилиния, подписи, тултип, легенда) ──
function LineChart({ periods, series, yLabel, isCount }: {
  periods: string[]; series: { name: string; color: string; values: number[] }[]; yLabel: string; isCount: boolean;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(900);
  const [hover, setHover] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = wrapRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const H = 380, padL = 56, padR = 20, padT = 24, padB = 60;
  const innerW = Math.max(w - padL - padR, 100);
  const innerH = H - padT - padB;
  const n = periods.length;
  const maxV = Math.max(1, ...series.flatMap((s) => s.values));
  const xAt = (i: number) => padL + (n <= 1 ? innerW / 2 : (innerW * i) / (n - 1));
  const yAt = (v: number) => padT + innerH - (v / maxV) * innerH;
  const ticks = 4;

  return (
    <div ref={wrapRef} className="relative w-full">
      <svg width={w} height={H} className="block">
        {/* сетка + ось Y */}
        {Array.from({ length: ticks + 1 }, (_, t) => {
          const v = (maxV / ticks) * t; const y = yAt(v);
          return (
            <g key={t}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="currentColor" className="text-line" strokeWidth={1} />
              <text x={padL - 8} y={y + 4} textAnchor="end" className="fill-accent text-[10px]">{fmtVal(v, isCount)}</text>
            </g>
          );
        })}
        <text x={14} y={padT + innerH / 2} textAnchor="middle" transform={`rotate(-90 14 ${padT + innerH / 2})`} className="fill-accent text-[11px]">{yLabel}</text>

        {/* подписи X */}
        {periods.map((p, i) => (
          <text key={p} x={xAt(i)} y={H - padB + 18} textAnchor="end" transform={`rotate(-45 ${xAt(i)} ${H - padB + 18})`} className="fill-ink-3 text-[10px]">{p}</text>
        ))}

        {/* направляющая при наведении */}
        {hover != null && <line x1={xAt(hover)} y1={padT} x2={xAt(hover)} y2={padT + innerH} stroke="currentColor" className="text-ink-3/40" strokeDasharray="4 4" />}

        {/* линии (сглаженные) */}
        {series.map((s, si) => (
          <g key={si}>
            <path fill="none" stroke={s.color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round"
              d={smoothPath(s.values.map((v, i) => [xAt(i), yAt(v)]))} />
            {s.values.map((v, i) => (
              <g key={i}>
                <circle cx={xAt(i)} cy={yAt(v)} r={3} fill={s.color} />
                {n <= 40 && (
                  <g transform={`translate(${xAt(i)}, ${yAt(v) - 12})`}>
                    <rect x={-16} y={-9} width={32} height={14} rx={3} fill={s.color} opacity={0.95} />
                    <text x={0} y={1} textAnchor="middle" className="fill-white text-[9px] font-semibold">{fmtVal(v, isCount)}</text>
                  </g>
                )}
              </g>
            ))}
          </g>
        ))}

        {/* зоны наведения */}
        {periods.map((_, i) => (
          <rect key={i} x={xAt(i) - innerW / (2 * Math.max(n, 1))} y={padT} width={innerW / Math.max(n, 1)} height={innerH}
            fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
        ))}
      </svg>

      {/* тултип */}
      {hover != null && (
        <div className="absolute pointer-events-none bg-surface-1 border border-line rounded-lg shadow-pop px-3 py-2 text-xs z-10"
          style={{ left: Math.min(xAt(hover) + 12, w - 180), top: padT + 10 }}>
          <div className="font-semibold text-ink-2 mb-1">{periods[hover]}</div>
          {series.map((s, si) => (
            <div key={si} className="flex items-center gap-2 py-0.5">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-ink-2">{s.name}:</span>
              <span className="font-semibold text-ink-1 tabular-nums">{isCount ? s.values[hover] : s.values[hover].toLocaleString('ru-RU')}</span>
            </div>
          ))}
        </div>
      )}

      {/* легенда */}
      <div className="flex items-center justify-center gap-5 mt-2">
        {series.map((s, si) => (
          <span key={si} className="flex items-center gap-1.5 text-xs text-ink-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} /> {s.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      <span className="block text-[11px] text-ink-3 mb-0.5">{label}</span>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none" />
      <input type="date" className="input pl-9" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// Сглаженная кривая через точки (кардинальный сплайн → кубические Безье).
function smoothPath(pts: [number, number][]): string {
  if (pts.length === 0) return '';
  if (pts.length < 3) return `M${pts.map((p) => `${p[0]},${p[1]}`).join(' L')}`;
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  return d;
}

// ── helpers ──
function fmtVal(v: number, isCount: boolean): string {
  if (isCount) return String(Math.round(v));
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  if (a >= 1e3) return `${Math.round(v / 1e3)}K`;
  return String(Math.round(v));
}
function aggregate(amts: number[], agg: Agg): number {
  if (agg === 'count') return amts.length;
  if (amts.length === 0) return 0;
  if (agg === 'sum') return amts.reduce((a, b) => a + b, 0);
  if (agg === 'avg') return amts.reduce((a, b) => a + b, 0) / amts.length;
  if (agg === 'max') return Math.max(...amts);
  return Math.min(...amts);
}
function isoWeek(ts: number): string {
  const d = new Date(ts); d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  const week = 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${d.getFullYear()}-${String(week).padStart(2, '0')}`;
}
function bucketKey(ts: number, g: GroupBy): string {
  const d = new Date(ts);
  if (g === 'day') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (g === 'week') return isoWeek(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function genPeriods(from: string, to: string, g: GroupBy, tss: number[]): string[] {
  let start = from ? new Date(from).getTime() : (tss.length ? Math.min(...tss) : Date.now());
  let end = to ? new Date(to).getTime() : (tss.length ? Math.max(...tss) : Date.now());
  if (start > end) [start, end] = [end, start];
  const keys: string[] = [];
  const seen = new Set<string>();
  const d = new Date(start); d.setHours(0, 0, 0, 0);
  let guard = 0;
  while (d.getTime() <= end && guard++ < 2000) {
    const k = bucketKey(d.getTime(), g);
    if (!seen.has(k)) { seen.add(k); keys.push(k); }
    if (g === 'day') d.setDate(d.getDate() + 1);
    else if (g === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
  }
  return keys;
}
